import { type FC } from "react";

import type { FormField } from "@/shared/types/estateEssentials";

import { AddressBlock } from "./AddressBlock";
import { FieldCell, fieldByKey } from "./FieldCell";

interface DoNotMailFormProps {
  fields: FormField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
  deceasedFirstName: string;
  estateId?: number;
}

export const DoNotMailForm: FC<DoNotMailFormProps> = ({
  fields,
  values,
  errors,
  onChange,
  deceasedFirstName,
  estateId,
}) => {
  const v = (key: string) => values[key] ?? "";
  const e = (key: string) => {
    if (errors[key]) return true;
    const field = fieldByKey(fields, key);
    if (!field.required) return false;
    return !v(key).trim();
  };
  const f = (key: string) => fieldByKey(fields, key);

  const nameLabel = deceasedFirstName
    ? `${deceasedFirstName}'s information`
    : "Deceased's information";

  const relationshipLabel = deceasedFirstName ? `I am ${deceasedFirstName}'s...` : "I am their...";

  return (
    <div className="space-y-6">
      <p className="text-base font-medium text-primary">{nameLabel}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldCell
          field={f("date_of_birth")}
          value={v("date_of_birth")}
          error={e("date_of_birth")}
          onChange={onChange}
        />
        <FieldCell
          field={f("date_of_death")}
          value={v("date_of_death")}
          error={e("date_of_death")}
          onChange={onChange}
        />
      </div>

      <AddressBlock
        prefix="deceased"
        values={values}
        onFieldChange={onChange}
        hasError={e}
        estateId={estateId}
      />

      <div className="border-t border-primary pt-6">
        <p className="text-base font-medium text-primary mb-4">Your information</p>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldCell
              field={f("user_first_name")}
              value={v("user_first_name")}
              error={e("user_first_name")}
              onChange={onChange}
            />
            <FieldCell
              field={f("user_last_name")}
              value={v("user_last_name")}
              error={e("user_last_name")}
              onChange={onChange}
            />
          </div>

          <FieldCell
            field={{ ...f("relationship_to_deceased"), label: relationshipLabel }}
            value={v("relationship_to_deceased")}
            error={e("relationship_to_deceased")}
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
};
